import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { getMe } from '../lib/api';
import { Keyboard, BookOpen, BarChart3, Users, Zap, Target } from 'lucide-react';

const Landing = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if already logged in
    const checkAuth = async () => {
      try {
        const response = await getMe();
        const user = response.data;
        if (user.role === 'teacher') {
          navigate('/teacher');
        } else if (user.role === 'student') {
          navigate('/student');
        } else {
          navigate('/role-select');
        }
      } catch {
        setLoading(false);
      }
    };
    checkAuth();
  }, [navigate]);

  const handleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background overflow-hidden">
      {/* Hero Section */}
      <div className="relative">
        {/* Background Image */}
        <div 
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1760112783543-737c7721a824?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMGRpZ2l0YWwlMjBkYXRhJTIwZmxvdyUyMGJsdWUlMjBwdXJwbGV8ZW58MHx8fHwxNzY4MjY3MDE1fDA&ixlib=rb-4.1.0&q=85)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-background/50 via-background/80 to-background" />
        
        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 pt-20 pb-32">
          {/* Nav */}
          <nav className="flex items-center justify-between mb-20">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center neon-border">
                <Keyboard className="w-5 h-5 text-primary" />
              </div>
              <span className="font-heading text-xl font-bold tracking-tight">LitBlitz</span>
            </div>
            <Button 
              onClick={handleLogin}
              data-testid="login-btn"
              className="btn-primary px-6 py-2 rounded-sm"
            >
              Get Started
            </Button>
          </nav>

          {/* Hero Content */}
          <div className="max-w-4xl">
            <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-bold leading-tight mb-6">
              <span className="gradient-text">Master Typing.</span>
              <br />
              <span className="text-white">Excel in ELA.</span>
            </h1>
            <p className="text-lg sm:text-xl text-text-secondary max-w-2xl mb-10 font-body">
              AI-powered typing exercises aligned with Mississippi ELA standards. 
              Track progress, build skills, and unlock your students' potential.
            </p>
            <div className="flex flex-wrap gap-4">
              <Button 
                onClick={handleLogin}
                data-testid="hero-cta-btn"
                className="btn-primary px-8 py-3 text-lg rounded-sm"
              >
                Start Free
                <Zap className="ml-2 w-5 h-5" />
              </Button>
              <Button 
                variant="outline"
                className="btn-secondary px-8 py-3 text-lg rounded-sm"
                data-testid="learn-more-btn"
                onClick={() => document.getElementById('features').scrollIntoView({ behavior: 'smooth' })}
              >
                Learn More
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <section id="features" className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-4">
              Built for <span className="text-primary">Modern Classrooms</span>
            </h2>
            <p className="text-text-secondary text-lg max-w-2xl mx-auto">
              Everything teachers need to create engaging typing exercises 
              aligned with MS CCRS ELA standards.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
            {/* Large Feature Card */}
            <div className="md:col-span-8 card-glass p-8" data-testid="feature-ai-generation">
              <div className="flex items-start gap-6">
                <div className="w-14 h-14 rounded-xl bg-primary/20 flex items-center justify-center flex-shrink-0">
                  <Zap className="w-7 h-7 text-primary" />
                </div>
                <div>
                  <h3 className="font-heading text-xl font-bold mb-2">AI-Powered Content Generation</h3>
                  <p className="text-text-secondary mb-4">
                    Select an ELA objective, grade level, and duration. Our AI generates 
                    custom passages and comprehension quizzes in seconds.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-3 py-1 rounded-full bg-primary/10 text-primary text-sm">
                      MS CCRS Aligned
                    </span>
                    <span className="px-3 py-1 rounded-full bg-secondary/10 text-secondary text-sm">
                      K-12 Support
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Small Feature Card */}
            <div className="md:col-span-4 card-glass p-6" data-testid="feature-wpm">
              <div className="w-12 h-12 rounded-xl bg-secondary/20 flex items-center justify-center mb-4">
                <Target className="w-6 h-6 text-secondary" />
              </div>
              <h3 className="font-heading text-lg font-bold mb-2">Real-Time WPM</h3>
              <p className="text-text-secondary text-sm">
                Live typing speed tracking with accuracy scoring and progress indicators.
              </p>
            </div>

            {/* Medium Feature Cards */}
            <div className="md:col-span-4 card-glass p-6" data-testid="feature-classroom">
              <div className="w-12 h-12 rounded-xl bg-status-success/20 flex items-center justify-center mb-4">
                <Users className="w-6 h-6 text-status-success" />
              </div>
              <h3 className="font-heading text-lg font-bold mb-2">Classroom Management</h3>
              <p className="text-text-secondary text-sm">
                Create classes, share join codes, and manage student rosters with ease.
              </p>
            </div>

            <div className="md:col-span-4 card-glass p-6" data-testid="feature-quiz">
              <div className="w-12 h-12 rounded-xl bg-status-warning/20 flex items-center justify-center mb-4">
                <BookOpen className="w-6 h-6 text-status-warning" />
              </div>
              <h3 className="font-heading text-lg font-bold mb-2">Comprehension Quizzes</h3>
              <p className="text-text-secondary text-sm">
                5-question standardized test-style quizzes generated for each passage.
              </p>
            </div>

            <div className="md:col-span-4 card-glass p-6" data-testid="feature-reports">
              <div className="w-12 h-12 rounded-xl bg-primary/20 flex items-center justify-center mb-4">
                <BarChart3 className="w-6 h-6 text-primary" />
              </div>
              <h3 className="font-heading text-lg font-bold mb-2">Progress Reports</h3>
              <p className="text-text-secondary text-sm">
                Track WPM improvements, accuracy trends, and quiz scores over time.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <div className="card-glass p-12 neon-border">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-4">
              Ready to Transform Your Classroom?
            </h2>
            <p className="text-text-secondary text-lg mb-8">
              Join educators across Mississippi using LitBlitz to build 
              typing skills while reinforcing ELA objectives.
            </p>
            <Button 
              onClick={handleLogin}
              data-testid="cta-btn"
              className="btn-primary px-10 py-4 text-lg rounded-sm"
            >
              Get Started Free
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-white/5">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Keyboard className="w-5 h-5 text-primary" />
            <span className="font-heading font-bold">LitBlitz</span>
          </div>
          <p className="text-text-muted text-sm">
            © 2025 LitBlitz. Aligned with MS CCRS ELA Standards.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
